import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, FileText, Loader2, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Admin-only READ-ONLY page that lists every `invoice_number` in
 * `retailer_invoices` appearing more than once. Clicking a row expands it and
 * shows each copy's id, retailer, dates, linked dispatches, payments and
 * credit notes.
 *
 * This page never writes to the database. It calls
 * `GET /api/admin/duplicate-invoices-report` and renders the result verbatim.
 */
export default function DuplicateInvoiceReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // invoice_number -> bool

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/admin/duplicate-invoices-report');
      setReport(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to load duplicate invoice report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const toggle = (num) => setExpanded((prev) => ({ ...prev, [num]: !prev[num] }));

  return (
    <Layout title="Duplicate Invoice Report">
      <div className="space-y-4" data-testid="duplicate-invoice-report-page">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <AlertTriangle className="text-amber-600 shrink-0" size={16} />
                Read-only audit of <code className="text-xs bg-gray-100 px-1 rounded">retailer_invoices.invoice_number</code> values
                appearing more than once. This page never modifies any data.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadReport}
              disabled={loading}
              data-testid="refresh-report-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading && (
            <Card className="p-10 flex flex-col items-center gap-2 text-gray-500">
              <Loader2 className="animate-spin" />
              <span>Loading duplicate invoice report...</span>
            </Card>
          )}

          {!loading && error && (
            <Card className="p-6 bg-red-50 border-red-200 text-red-700" data-testid="report-error">
              {error}
            </Card>
          )}

          {!loading && !error && report && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Duplicate invoice numbers</p>
                  <p className="text-3xl font-bold text-amber-700" data-testid="total-duplicate-numbers">
                    {report.total_duplicate_numbers}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Extra rows to remove</p>
                  <p className="text-3xl font-bold text-red-700" data-testid="total-extra-rows">
                    {report.total_extra_rows}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">Σ (count − 1) across all groups</p>
                </Card>
                <Card className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Generated at</p>
                  <p className="text-sm font-medium mt-1" data-testid="generated-at">
                    {report.generated_at ? new Date(report.generated_at).toLocaleString() : '—'}
                  </p>
                </Card>
              </div>

              {report.groups.length === 0 ? (
                <Card className="p-10 text-center text-green-700 bg-green-50 border-green-200">
                  No duplicate invoice numbers found. ✅
                </Card>
              ) : (
                <Card className="p-0 overflow-hidden" data-testid="duplicate-groups-table">
                  {/* Horizontal scroll on narrow screens; vertical scroll keeps header
                      sticky via `max-h` + `overflow-y-auto` on the wrapper and
                      `position: sticky; top: 0` on <thead>. */}
                  <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
                    <table className="text-sm" style={{ tableLayout: 'fixed', minWidth: '960px', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '220px' }} />{/* Invoice Number */}
                        <col style={{ width: '220px' }} />{/* Retailer */}
                        <col style={{ width: '120px' }} />{/* Invoice Date */}
                        <col style={{ width: '80px' }} />{/* Copies */}
                        <col style={{ width: '180px' }} />{/* Total Paid Across Copies */}
                        <col style={{ width: '150px' }} />{/* Net Payable (First) */}
                        <col style={{ width: '110px' }} />{/* Details */}
                      </colgroup>
                      <thead className="bg-gray-100 text-gray-600 text-[11px] uppercase tracking-wide sticky top-0 z-10 shadow-sm">
                        <tr>
                          <th className="p-3 text-left font-semibold">Invoice Number</th>
                          <th className="p-3 text-left font-semibold">Retailer</th>
                          <th className="p-3 text-left font-semibold">Invoice Date</th>
                          <th className="p-3 text-center font-semibold">Copies</th>
                          <th className="p-3 text-right font-semibold">Total Paid<br/>Across Copies</th>
                          <th className="p-3 text-right font-semibold">Net Payable<br/>(First)</th>
                          <th className="p-3 text-center font-semibold">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.groups.map((g) => {
                          const isOpen = !!expanded[g.invoice_number];
                          const retailer = g.copies[0]?.retailer_name || '—';
                          const invoiceDate = g.copies[0]?.invoice_date || '—';
                          // Sum unique payment amounts across all copies (dedupe by payment id).
                          const uniquePayIds = new Set();
                          let totalPaidPayments = 0;
                          g.copies.forEach(c => c.payments.forEach(p => {
                            if (p.id && !uniquePayIds.has(p.id)) {
                              uniquePayIds.add(p.id);
                              totalPaidPayments += p.amount || 0;
                            }
                          }));
                          return (
                            <React.Fragment key={g.invoice_number}>
                              <tr
                                className="border-t cursor-pointer hover:bg-gray-50"
                                onClick={() => toggle(g.invoice_number)}
                                data-testid={`dup-group-row-${g.invoice_number}`}
                              >
                                <td className="p-3 font-mono text-[12px] text-[#14532D] truncate" title={g.invoice_number}>
                                  {g.invoice_number}
                                </td>
                                <td className="p-3 truncate" title={retailer}>
                                  {retailer}
                                </td>
                                <td className="p-3 text-gray-700 whitespace-nowrap">
                                  {invoiceDate}
                                </td>
                                <td className="p-3 text-center">
                                  <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                                    {g.count}
                                  </span>
                                </td>
                                <td className="p-3 text-right tabular-nums">
                                  {fmtMoney(totalPaidPayments)}
                                </td>
                                <td className="p-3 text-right tabular-nums">
                                  {fmtMoney(g.copies[0]?.net_payable)}
                                </td>
                                <td className="p-3 text-center">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggle(g.invoice_number); }}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-[#14532D] hover:underline"
                                    data-testid={`details-toggle-${g.invoice_number}`}
                                  >
                                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    {isOpen ? 'Hide' : 'Details'}
                                  </button>
                                </td>
                              </tr>
                              {isOpen && (
                                <tr className="bg-gray-50">
                                  <td colSpan={7} className="p-0">
                                    <div className="p-4" data-testid={`dup-group-detail-${g.invoice_number}`}>
                                      <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
                                        <colgroup>
                                          <col style={{ width: '300px' }} />{/* Invoice ID */}
                                          <col style={{ width: '170px' }} />{/* Revised Date */}
                                          <col style={{ width: '130px' }} />{/* Paid Amount */}
                                          <col />{/* Linked Credit Notes */}
                                        </colgroup>
                                        <thead className="bg-white text-[10px] uppercase tracking-wide text-gray-500 border-b">
                                          <tr>
                                            <th className="p-2 text-left font-semibold">Invoice ID</th>
                                            <th className="p-2 text-left font-semibold">Revised Date</th>
                                            <th className="p-2 text-right font-semibold">Paid Amount</th>
                                            <th className="p-2 text-left font-semibold">Linked Credit Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {g.copies.map((c) => {
                                            const revisedDate = c.created_at
                                              ? new Date(c.created_at).toLocaleDateString(undefined, {
                                                  day: '2-digit', month: 'short', year: 'numeric',
                                                })
                                              : '—';
                                            const cns = [
                                              ...c.credit_notes_created_from.map((cn) => ({ ...cn, _kind: 'from' })),
                                              ...c.credit_notes_adjusted_in.map((cn) => ({ ...cn, _kind: 'adj' })),
                                            ];
                                            return (
                                              <tr key={c.id} className="border-b last:border-b-0 bg-white">
                                                <td className="p-2 font-mono text-[11px] text-gray-700 break-all" title={c.id}>
                                                  {c.id}
                                                </td>
                                                <td className="p-2 text-gray-700 whitespace-nowrap">
                                                  {revisedDate}
                                                  {c.revision_number != null && (
                                                    <span className="ml-1 text-[10px] text-gray-400">(rev {c.revision_number})</span>
                                                  )}
                                                </td>
                                                <td className="p-2 text-right tabular-nums">
                                                  {fmtMoney(c.paid_amount)}
                                                </td>
                                                <td className="p-2">
                                                  {cns.length === 0 && <span className="text-gray-400">none</span>}
                                                  <div className="flex flex-wrap gap-1">
                                                    {cns.map((cn) => (
                                                      <span
                                                        key={`${cn._kind}-${cn.id}`}
                                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono ${
                                                          cn._kind === 'from'
                                                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                            : 'bg-blue-50 border-blue-200 text-blue-800'
                                                        }`}
                                                        title={`${cn._kind === 'from' ? 'Created from this copy' : 'Adjusted into this copy'} · amount ${fmtMoney(cn.amount)} · status ${cn.status || '—'}`}
                                                      >
                                                        {cn.credit_note_number}
                                                        <span className="opacity-70">{fmtMoney(cn.amount)}</span>
                                                      </span>
                                                    ))}
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              <Card className="p-4 bg-blue-50 border-blue-200 text-xs text-blue-900 flex items-start gap-2">
                <FileText size={14} className="mt-0.5 shrink-0" />
                <div>
                  This is a <strong>read-only</strong> audit view. No invoice, payment, credit note, dispatch or any other
                  record is touched by opening or refreshing this page. The equivalent CLI report is
                  at <code className="bg-white/70 px-1 rounded">backend/scripts/dry_run_duplicate_invoices.py</code>.
                </div>
              </Card>
            </>
          )}
      </div>
    </Layout>
  );
}
